import React from 'react';
import { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import Footer from '../components/Footer';
import Header from '../components/Header';

// Define the plans based on PDF content
const Plans = [
    {
        title: 'Estate Planning & Trust Strategy',
        icon: 'assets/images/icons/estate-planning.png',
        description: `
Estate planning is critical for protecting your wealth, family, and legacy. It involves organizing how your assets will be managed and distributed after death or incapacitation using tools like wills, trusts, powers of attorney, and beneficiary designations. Over 60% of Americans lack an estate plan, risking probate delays, fees, and family conflicts.

**Why It Matters**  
- **Individuals**: Direct asset distribution and avoid probate.  
- **Families**: Protect minor children and family wealth.  
- **Business Owners**: Ensure business continuity and reduce tax liability.  
- **High-Net-Worth**: Use trusts to shield estates from taxes (2025 threshold: $13.61M individual, $27.22M married).

**Key Tools**  
- **Will**: Specifies asset distribution.  
- **Durable Power of Attorney**: Manages finances if incapacitated.  
- **Healthcare Proxy**: Appoints medical decision-makers.  
- **Trusts**: Avoid probate, ensure privacy, and reduce taxes.  

**Trust Types**  
- **Revocable Living Trust**: Control during life, avoids probate, tax-neutral.  
- **Irrevocable Trust**: Tax and asset protection, cannot be changed.  
- **Special Needs Trust**: Preserves government benefits for disabled individuals.  
- **Charitable Remainder Trust**: Income for donor, remainder to charity.

**Assets in Trusts**  
Real estate, bank accounts, investments, LLC ownership, life insurance, and valuable property can be held in trusts for centralized control and tax efficiency.  
        `,
        image: 'assets/images/WE1.jpg'
    },
    {
        title: 'Budgeting',
        icon: 'assets/images/icons/budgeting.png',
        description: `
Budgeting is the cornerstone of financial planning, ensuring stability and goal achievement for individuals, entrepreneurs, and businesses. Only 41% of Americans consistently use a budget, yet 67% feel more in control with one.

**For Individuals**  
- Track income vs. expenses.  
- Prioritize savings and debt repayment.  
- Use the 50/30/20 rule: 50% needs, 30% wants, 20% savings/debt payoff.

**For Self-Employed**  
- Separate business and personal accounts.  
- Budget based on client pipeline.  
- Allocate 25-30% for taxes and save for slow months.  
- Tools: QuickBooks, Wave, budget templates.

**For Businesses**  
- Break down fixed vs. variable expenses.  
- Forecast revenue and track burn rate.  
- Allocate funds for payroll, marketing, and reserves.

**Best Practices**  
- Use apps like YNAB, Mint, or EveryDollar.  
- Set SMART goals and automate savings.  
- Review budgets monthly and track cash flow weekly.  
        `,
        image: 'assets/images/WE2.jpg'
    },
    {
        title: 'Debt Payoff Strategies',
        icon: 'assets/images/icons/debt-payoff.png',
        description: `
Eliminating debt reduces stress, increases net worth, and opens opportunities. The average U.S. adult carries significant debt, including $28,700 in credit cards and $255,000 in mortgages.

**Strategies for Individuals**  
- **Snowball Method**: Pay smallest debts first for momentum.  
- **Avalanche Method**: Target high-interest debts to save on interest.  
- **Balance Transfers**: Use 0% APR offers for 12-18 months.  
- **Debt Consolidation**: Combine debts into a lower-interest loan.

**Mortgage & Auto Loans**  
- Round up payments or pay bi-weekly.  
- Refinance when rates drop significantly.  
- Use windfalls to reduce principal (e.g., $2,000 extra/month on a $300,000 mortgage saves $84,000+ in interest).

**Bankruptcy Options**  
- **Chapter 7**: Liquidates unsecured debts, impacts credit for 10 years.  
- **Chapter 13**: 3-5 year repayment plan, impacts credit for 7 years.  
- **Pros**: Immediate relief, legal protection.  
- **Cons**: Credit damage, legal fees, and borrowing challenges.

**Best Practices**  
- Track all debts and automate minimum payments.  
- Avoid new debt and increase income via side hustles.  
- Consult credit counselors when needed.  
        `,
        image: 'assets/images/WE3.jpg'
    },
    {
        title: 'Net Worth',
        icon: 'assets/images/icons/net-worth.png',
        description: `
Net worth (assets minus liabilities) is a key indicator of financial health, yet often untracked. It helps measure growth, plan for retirement, and protect your legacy.

**Assets**  
- Cash, savings, retirement accounts, investments, real estate, business equity, vehicles, and personal property.

**Liabilities**  
- Credit card debt, student loans, auto loans, mortgages, and business loans.

**Why It Matters**  
- Tracks financial progress over time.  
- Guides retirement and investment strategies.  

**How to Calculate**  
1. List assets with current values.  
2. List liabilities with outstanding balances.  
3. Subtract liabilities from assets.

**Growth Strategies**  
- Increase income via business or investments.  
- Reduce liabilities through refinancing or trade-ins.  
- Save aggressively using apps like Personal Capital or YNAB.  
- Update net worth quarterly to reflect changes.  
        `,
        image: 'assets/images/WE4.jpg'
    },
    {
        title: 'Savings Goal',
        icon: 'assets/images/icons/savings.png',
        description: `
Savings are the foundation of wealth creation, providing security and opportunity. Only 37% of Americans can cover a $400 emergency without borrowing.

**Types of Savings**  
- **Emergency Fund**: 3-6 months of expenses in high-yield accounts (4.2-5.1% APY).  
- **Retirement Savings**: 401(k), IRA, or SEP IRA.  
- **Business Reserves**: 3 months of operating expenses.  
- **Investment Savings**: For real estate or business ventures.  
- **Short-Term Goals**: Travel, tuition, or vehicles.

**Savings Goals by Income**  
- **$50K**: $5,000+ emergency fund.  
- **$50K-$100K**: 6 months of expenses ($15K-$30K).  
- **$100K+**: 6-12 months cash plus investment savings.

**Best Practices**  
- Automate weekly transfers to high-yield savings.  
- Use tiered buckets: short-term (1 year), mid-term (2-5 years), long-term (5+ years).  
- For businesses, maintain tax savings (25-35%) and growth reserves.  
- Think of savings as paying your future self.  
        `,
        image: 'assets/images/WE5.jpg'
    },
    {
        title: 'Retirement & IRA Strategies',
        icon: 'assets/images/icons/retirement.png',
        description: `
IRAs and Solo 401(k)s are tax-advantaged tools for building long-term wealth, especially for self-employed individuals and small business owners.

**Types of IRAs**  
- **Traditional IRA**: Pre-tax contributions, taxed on withdrawal.  
- **Roth IRA**: Post-tax contributions, tax-free growth (2025 income limits: $161K single, $240K married).  
- **SEP IRA**: Employer-funded for self-employed, up to $69,000 (2025).  
- **Self-Directed IRA**: Invest in real estate, crypto, or private equity.  
- **Backdoor Roth**: Convert Traditional IRA for high earners.

**Solo 401(k)**  
- Dual contributions: $23,000 employee deferral + 25% employer contribution (e.g., $48,000 total for $100K income).  
- Offers Roth and loan options, ideal for self-employed.

**Growth Projections**  
- Roth IRA ($6,500/yr at 7%): $39,300 in 5 years, $225,500 in 20 years.  
- SEP IRA ($25,000/yr at 7%): $143,300 in 5 years, $1,225,500 in 20 years.

**Best Practices**  
- Open IRAs before April 15 for prior-year benefits.  
- Use custodians like Fidelity or E*TRADE for checkbook control.  
- Integrate with payroll and tax planning to avoid prohibited transactions.  
        `,
        image: 'assets/images/WE6.jpg'
    }
];

export default function WealthEducation() {
    const [isLoading, setIsLoading] = useState(true);
    const [selectedPlan, setSelectedPlan] = useState(null);
    const [showScrollTop, setShowScrollTop] = useState(false);
    const location = useLocation();
    const navigate = useNavigate();

    const toSlug = (text) =>
        text
            .toLowerCase()
            .replace(/&/g, 'and')
            .replace(/[^a-z0-9]+/g, '-')
            .replace(/^-+|-+$/g, '');

    const getSlugFromLocation = (loc) => {
        if (loc.hash && loc.hash.length > 1) {
            return loc.hash.substring(1).toLowerCase();
        }
        const params = new URLSearchParams(loc.search);
        const tab = params.get('tab');
        return tab ? tab.toLowerCase() : null;
    };

    // Initial loading fade-in and initial plan selection from URL (hash or ?tab=)
    useEffect(() => {
        setTimeout(() => {
            setIsLoading(false);
        }, 1000);
    }, []);

    // Keep selected plan in sync with URL changes (hash or ?tab=)
    useEffect(() => {
        const slug = getSlugFromLocation(location);
        if (slug) {
            const matched = Plans.find((p) => toSlug(p.title) === slug);
            if (matched && matched.title !== selectedPlan?.title) {
                setSelectedPlan(matched);
                return;
            }
        }
        // Fallback to first plan if none selected yet
        if (!slug && !selectedPlan) {
            setSelectedPlan(Plans[0]);
        }
    }, [location]);

    // Show scroll-to-top button after scrolling down 200px
    useEffect(() => {
        const handleScroll = () => {
            setShowScrollTop(window.scrollY > 200);
        };
        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    return (
        <div className="boxed_wrapper ltr">
            <style>
                {`
                    .sidebar-box {
                        background-color: #f5f5f5;
                        border-radius: 8px;
                        padding: 20px;
                        margin-bottom: 20px;
                    }
                    .sidebar-menu {
                        list-style: none;
                        padding: 0;
                        margin: 0;
                    }
                    .sidebar-menu li {
                        display: flex;
                        align-items: center;
                        padding: 12px 15px;
                        margin-bottom: 8px;
                        border-radius: 6px;
                        font-size: 16px;
                        font-weight: 500;
                        color: #333;
                        background-color: #ffffff;
                        box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
                        cursor: pointer;
                        transition: all 0.3s ease;
                    }
                    .sidebar-menu li:hover {
                        background-color: #e8ecef;
                        color: #007bff;
                    }
                    .sidebar-menu li.active {
                        background-color: #f7bf68;
                        color: #ffffff;
                        font-weight: 600;
                    }
                    .markdown-content h3 {
                        font-size: 1.5rem;
                        font-weight: 600;
                        color: #1a202c;
                        margin-bottom: 1rem;
                    }
                    .markdown-content p {
                        color: #4a4a4a;
                        line-height: 1.6;
                        margin-bottom: 1rem;
                    }
                    .markdown-content ul {
                        list-style: disc;
                        margin-left: 1.5rem;
                        margin-bottom: 1rem;
                    }
                    .markdown-content li {
                        margin-bottom: 0.5rem;
                    }
                    .markdown-content strong {
                        font-weight: 600;
                        color: #2d3748;
                    }
                    .page-title {
                        background-size: cover;
                        background-position: center;
                        position: relative;
                        height: 250px;
                        display: flex;
                        align-items: center;
                    }
                    .page-title .bg-layer::before {
                        content: '';
                        position: absolute;
                        inset: 0;
                        background-color: rgba(0, 0, 0, 0.5);
                    }
                    .scroll-to-top-btn {
                        position: fixed;
                        bottom: 30px;
                        right: 30px;
                        width: 48px;
                        height: 48px;
                        background: #f7bf68;
                        color: #fff;
                        border: none;
                        border-radius: 50%;
                        box-shadow: 0 2px 8px rgba(0,0,0,0.15);
                        display: flex;
                        align-items: center;
                        justify-content: center;
                        font-size: 1.5rem;
                        cursor: pointer;
                        z-index: 1000;
                        transition: background 0.2s;
                    }
                    .scroll-to-top-btn:hover {
                        background: #e0a800;
                    }
                    .image-wrapper {
                        position: relative;
                        overflow: hidden;
                        border-radius: 8px;
                        margin: 20px auto;
                        max-width: 50%;
                    }
                    .content-image {
                        width: 100%;
                        height: auto;
                        border-radius: 8px;
                        box-shadow: 0 5px 15px rgba(0, 0, 0, 0.05);
                        max-height: 300px; /* Reduced height */
                        object-fit: cover;
                    }
                    .image-overlay {
                        position: absolute;
                        top: 0;
                        left: 0;
                        width: 100%;
                        height: 100%;
                        background: rgba(0, 0, 0, 0.7);
                        color: white;
                        padding: 20px;
                        border-radius: 8px;
                        transform: translateY(100%);
                        opacity: 0;
                        transition: transform 0.3s ease, opacity 0.3s ease;
                        display: flex;
                        flex-direction: column;
                        justify-content: flex-end;
                        overflow-y: auto;
                    }
                    .image-wrapper:hover .image-overlay {
                        transform: translateY(0);
                        opacity: 1;
                    }
                    .image-overlay h4 {
                        margin: 0 0 10px;
                        font-size: 18px;
                        color: #f7bf68;
                    }
                    .image-overlay ul {
                        list-style: none;
                        padding: 0;
                        margin: 0;
                    }
                    .image-overlay li {
                        margin-bottom: 8px;
                        position: relative;
                        padding-left: 20px;
                    }
                    .image-overlay li:before {
                        content: '•';
                        color: #f7bf68;
                        position: absolute;
                        left: 0;
                        font-weight: bold;
                    }
                    @media (max-width: 768px) {
                        .sidebar-box {
                            margin-bottom: 30px;
                        }
                        .sidebar-menu li {
                            font-size: 14px;
                            padding: 10px 12px;
                        }
                        .page-title {
                            height: 200px;
                        }
                        .page-title h1 {
                            font-size: 1.75rem;
                        }
                        .scroll-to-top-btn {
                            width: 40px;
                            height: 40px;
                            font-size: 1.2rem;
                            bottom: 20px;
                            right: 20px;
                        }
                        .image-wrapper {
                            max-width: 100%;
                        }
                        .content-image {
                            max-height: 200px; /* Reduced height for mobile */
                        }
                    }
                    .page-title .auto-container, .page-title .content-box, .page-title h1, .page-title .bread-crumb {
                        text-align: left !important;
                    }
                `}
            </style>
            {isLoading && (
                <div className="loader-wrap">
                    <div className="preloader">
                        <div className="preloader-close">close</div>
                        <div id="handle-preloader" className="handle-preloader">
                            <div className="animation-preloader">
                                <div className="txt-loading">
                                    <img src="assets/images/animation_logo.gif" alt="img" />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}
            <div>
                <div id="search-popup" className="search-popup">
                    <div className="popup-inner">
                        <div className="upper-box">
                            <figure className="logo-box">
                                <a href="index.html">
                                    <img src="assets/images/logo-2.png" alt="" />
                                </a>
                            </figure>
                            <div className="close-search">
                                <span className="fas fa-times"></span>
                            </div>
                        </div>
                        <div className="overlay-layer"></div>
                        <div className="auto-container">
                            <div className="search-form">
                                <form method="post" action="index.html">
                                    <div className="form-group">
                                        <fieldset>
                                            <input
                                                type="search"
                                                className="form-control"
                                                name="search-input"
                                                defaultValue="Read-only content"
                                                placeholder="Type your keyword and hit"
                                                required
                                            />
                                            <button type="submit">
                                                <i className="fas fa-search"></i>
                                            </button>
                                        </fieldset>
                                    </div>
                                </form>
                            </div>
                        </div>
                    </div>
                </div>
                <Header />
                {/* page-title */}
                <section className="page-title">
                    <div
                        className="bg-layer"
                        style={{ backgroundImage: `url("assets/images/background/page-title.jpg")` }}
                    ></div>
                    <div className="line-grid-wrap">
                        <span className="line-grid-1"></span>
                        <span className="line-grid-2"></span>
                        <span className="line-grid-3"></span>
                        <span className="line-grid-4"></span>
                        <span className="line-grid-5"></span>
                    </div>
                    <div className="auto-container">
                        <div className="content-box">
                            <h1>Wealth Education</h1>
                            <ul className="bread-crumb clearfix">
                                <li>
                                    <Link to="/">Home</Link>
                                </li>
                                <li>Wealth Education</li>
                            </ul>
                        </div>
                    </div>
                </section>
                {/* page-title end */}

                {/* about-style-four */}
                <section className="about-style-four home-23 pt_110 pb_120" style={{ textAlign: 'left' }}>
                    <div className="auto-container">
                        <div className="row clearfix">
                            <div className="col-lg-12 col-md-12 col-sm-12 content-column">
                                <div className="content_block_three">
                                    <div className="content-box">
                                        <div className="sec-title mb_25">
                                            <span className="sub-title mb_14">Wealth Education</span>
                                            <h2>Empowering Your Financial Future</h2>
                                        </div>
                                        <div className="text-box">
                                            <p>
                                                At Ravline, we believe that financial literacy is the foundation of
                                                long-term success and stability. Our Wealth Education program is
                                                designed to provide individuals and businesses with the tools,
                                                knowledge, and resources needed to make informed financial decisions.
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className="row clearfix">
                            {/* Sidebar */}
                            <div className="col-lg-3 col-md-4 col-sm-12 sidebar-column">
                                <div className="sidebar-box">
                                    <ul className="sidebar-menu">
                                        {Plans.map((plan, index) => (
                                            <li
                                                key={index}
                                                className={selectedPlan?.title === plan.title ? 'active' : ''}
                                                onClick={() => {
                                                    setSelectedPlan(plan);
                                                    const slug = toSlug(plan.title);
                                                    navigate(`${location.pathname}#${slug}`);
                                                }}
                                            >
                                                {plan.title}
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            </div>
                            {/* Content */}
                            <div className="col-lg-9 col-md-8 col-sm-12 content-column">
                                {selectedPlan && (
                                    <div className="content-box markdown-content">
                                        <h3>{selectedPlan.title}</h3>
                                        <div className="image-wrapper">
                                            <img
                                                src={selectedPlan.image}
                                                alt={`${selectedPlan.title} Overview`}
                                                className="content-image"
                                                width="400" // Reduced width
                                                height="250" // Reduced height
                                            />
                                            <div className="image-overlay">
                                                <h4>{selectedPlan.title} Insights</h4>
                                                <ul>
                                                    <li>Enhances financial control.</li>
                                                    <li>Reduces risks and costs.</li>
                                                    <li>Supports long-term growth.</li>
                                                </ul>
                                            </div>
                                        </div>
                                        <div
                                            dangerouslySetInnerHTML={{
                                                __html: selectedPlan.description
                                                    .replace(/\n/g, '<br>')
                                                    .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
                                            }}
                                        />
                                        <p>
                                            This section provides additional details and practical steps to implement the
                                            strategies outlined above. Regular review and adjustment are key to
                                            maintaining financial health.
                                        </p>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </section>
                {/* about-style-four end */}

                <Footer />

                {/* Scroll to top */}
                {showScrollTop && (
                    <button
                        className="scroll-to-top-btn"
                        onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                        aria-label="Scroll to top"
                    >
                        <i className="fas fa-arrow-up"></i>
                    </button>
                )}
                {/* Scroll to top end */}
            </div>
        </div>
    );
}